import React, { useEffect, useRef } from 'react';
import { Pill as DesignSystemPill } from '@lmn/design-system';
import {
  PillClearButton,
  PillCountBadge,
  PillCustomWrapper,
  PillHiddenLabel,
} from '@/app/common/components/Pill/styled.ts';
import { IconError } from '@/app/common/icons/libraryIcons';

type FilterPillsProps = {
  label?: string | React.ReactElement;
  icon?: React.ReactElement;
  handleClick?: () => void;
  /** Inert but still named, focusable and aria-disabled while it waits. */
  busy?: boolean;
  selected?: boolean;
  /** Wears the loading skeleton, keeping its name out of sight. */
  isLoading: boolean;
  isOpen: boolean;
  /** Opens an overlay: earns aria-expanded and aria-haspopup. */
  hasPopover: boolean;
  /** Whether that overlay is advertised with a chevron. Defaults to showing one. */
  showChevron?: boolean;
  /** Announces aria-pressed. A pill that acts rather than toggles says so by passing false. */
  toggles?: boolean;
  filtersCount?: number;
  /** Shows filtersCount as a filled badge rather than "(n)". */
  countBadge?: boolean;
  /** Shows only the icon and count, keeping the label for assistive tech. */
  iconOnly?: boolean;
  /** Clicking clears the filter, so the design system's clear icon earns its place. */
  removes?: boolean;
  /** A clear control of its own, beside a trigger that still opens the overlay. */
  handleClear?: () => void;
  clearLabel?: string;
  ref?: React.Ref<HTMLDivElement>;
  className?: string;
  controls?: string;
};

export const DESIGN_SYSTEM_TRIGGER = 'button:not([data-pill-clear])';

const setOrRemove = (node: Element, name: string, value: string | undefined) => {
  if (value === undefined) node.removeAttribute(name);
  else node.setAttribute(name, value);
};

const noop = () => {};

const Pill = ({
  label,
  icon,
  handleClick,
  busy,
  selected,
  isLoading,
  isOpen,
  hasPopover,
  showChevron = true,
  toggles,
  filtersCount,
  countBadge,
  iconOnly,
  removes,
  handleClear,
  clearLabel,
  ref,
  className,
  controls,
}: FilterPillsProps) => {
  const inert = !!busy || isLoading;
  const skeleton = isLoading;

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const forwarded = ref as React.RefCallback<HTMLDivElement> | React.MutableRefObject<HTMLDivElement | null> | null;

  const attachRef = (node: HTMLDivElement | null) => {
    wrapperRef.current = node;
    if (typeof forwarded === 'function') forwarded(node);
    else if (forwarded) forwarded.current = node;
  };

  // Every pill the design system builds without a popover is a toggle, and says so.
  const announcesPressed = toggles ?? (!hasPopover && !removes);

  // The three the design system has no prop for; it owns role, aria-expanded, aria-pressed and aria-haspopup.
  useEffect(() => {
    const trigger = wrapperRef.current?.querySelector(DESIGN_SYSTEM_TRIGGER);
    if (!trigger) return;

    setOrRemove(trigger, 'aria-controls', controls);
    setOrRemove(trigger, 'aria-disabled', inert ? 'true' : undefined);
    if (!announcesPressed) trigger.removeAttribute('aria-pressed');
  }, [controls, inert, announcesPressed, selected, hasPopover, isLoading]);

  const badge = !!filtersCount && !!countBadge;
  const shownLabel = iconOnly ? <PillHiddenLabel>{label}</PillHiddenLabel> : (label ?? '');
  const content = badge ? (
    <>
      {shownLabel}
      <PillCountBadge>{filtersCount}</PillCountBadge>
    </>
  ) : (
    shownLabel
  );

  const popoverProps = hasPopover
    ? ({ hasPopover: true, isOpen, selectedCount: countBadge ? undefined : filtersCount } as const)
    : ({ hasPopover: false, isSelected: selected } as const);

  const clears = !!handleClear && !skeleton;

  // Hidden rather than suppressed in the markup, which would take aria-pressed with it.
  const rendersStateIcon = hasPopover || !!selected;
  const hideStateIcon = rendersStateIcon && (hasPopover ? !showChevron || !!iconOnly : !removes);

  return (
    <PillCustomWrapper
      $selected={selected}
      $enabled={!inert}
      $loading={skeleton}
      $open={isOpen}
      $hideStateIcon={hideStateIcon}
      $clears={clears}
      $iconOnly={iconOnly}
      $iconAlone={iconOnly && !filtersCount}
      ref={attachRef}
      className={className}
      data-loading={skeleton || undefined}
    >
      <DesignSystemPill
        label={skeleton ? <PillHiddenLabel data-testid="pill-hidden-label">{label}</PillHiddenLabel> : content}
        iconLeft={skeleton ? undefined : icon}
        onClick={inert ? noop : (handleClick ?? noop)}
        {...(skeleton ? ({ hasPopover: false, isSelected: false } as const) : popoverProps)}
      />
      {clears && (
        <PillClearButton
          type="button"
          data-pill-clear=""
          aria-label={clearLabel}
          aria-disabled={inert || undefined}
          onClick={inert ? noop : handleClear}
        >
          <IconError size={22} />
        </PillClearButton>
      )}
    </PillCustomWrapper>
  );
};

export default Pill;
